import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Package, Truck, CheckCircle, XCircle, Clock, MapPin, HelpCircle, Phone, Wallet, CreditCard, Info } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import OnboardingTutorial, { useOnboarding } from "@/components/OnboardingTutorial";
import AboutArteknosModal from "@/components/AboutArteknosModal";
import { DeliverySharingToggle } from "@/components/DeliverySharingToggle";
import useAboutOnce from "@/hooks/useAboutOnce";
import AddRoleCard from "@/components/AddRoleCard";
import { AccountMenu } from "@/components/AccountMenu";
import { ClientMapPanel } from "@/components/map/ClientMapPanel";
import { WalletWaveCard } from "@/components/wallet/WalletWaveCard";
import RoleSwitcher from "@/components/RoleSwitcher";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ user, onLogout, onUserUpdate }) {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 400);
    return () => clearTimeout(t);
  }, [hash]);
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [creditBalance, setCreditBalance] = useState(0);
  const { showOnboarding, completeOnboarding, resetOnboarding } = useOnboarding(user?.role);
  const aboutModal = useAboutOnce(user?.id);

  useEffect(() => {
    fetchOrders();
    fetchCreditBalance();
    saveUserLocation();
  }, []);

  const saveUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            await axios.post(`${API}/gps/user-location`, {
              user_id: user.id,
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            });
          } catch (error) {
            console.error("GPS save error:", error);
          }
        },
        (error) => console.error("Geolocation error:", error),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  };

  const fetchCreditBalance = async () => {
    try {
      const response = await axios.get(`${API}/credits/balance/${user.id}`);
      setCreditBalance(response.data.credit_balance || 0);
    } catch (error) {
      console.error("Error fetching credit balance:", error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders?role=client&customer_email=${user.email}&customer_id=${user.id}`);
      setOrders(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des commandes");
    } finally {
      setIsLoading(false);
    }
  };

  const callCourier = async (orderId) => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/contact?requester_role=client`);
      const phone = response.data.contact_phone;
      if (phone) {
        window.location.href = `tel:${phone}`;
      } else {
        toast.error("Numéro du livreur non disponible");
      }
    } catch (error) {
      toast.error("Impossible de contacter le livreur");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "delivered": return <CheckCircle className="w-5 h-5 text-green-600" />;
      case "cancelled": return <XCircle className="w-5 h-5 text-red-600" />;
      case "in_transit":
      case "picked_up": return <Truck className="w-5 h-5 text-orange-600" />;
      case "assigned": return <Truck className="w-5 h-5 text-[#003594]" />;
      case "ready": return <Package className="w-5 h-5 text-cyan-600" />;
      case "preparing": return <Package className="w-5 h-5 text-[#003594]" />;
      default: return <Clock className="w-5 h-5 text-yellow-600" />;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: "En attente", preparing: "En préparation", ready: "Prête",
      assigned: "Livreur assigné", picked_up: "Colis récupéré",
      in_transit: "En livraison", delivered: "Livrée", cancelled: "Annulée"
    };
    return labels[status] || status;
  };

  const canTrackOrder = (status) => {
    return ['assigned', 'picked_up', 'in_transit', 'delivered'].includes(status);
  };

  const hasActiveCourier = (order) => {
    return order.courier_id && ['assigned', 'picked_up', 'in_transit'].includes(order.status);
  };

  return (
    <div className="min-h-screen bg-[#0F172A] text-white pb-10">
      <AboutArteknosModal open={aboutModal.open} onClose={aboutModal.close} />
      {showOnboarding && (
        <OnboardingTutorial
          userRole={user?.role || 'client'}
          userName={user?.full_name}
          onComplete={completeOnboarding}
        />
      )}

      {/* Nav luxury */}
      <nav className="bg-[#1E293B] border-b border-white/5 px-4 md:px-8 lg:px-12 py-4 rounded-b-[3rem] shadow-2xl">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img
              src="/woma-logo.png"
              alt="WÔMA de ARTEKNOS"
              className="w-10 h-10 rounded-full object-cover ring-2 ring-[#D4AF37]/50 shadow-[0_0_12px_rgba(212,175,55,0.3)]"
              data-testid="dashboard-logo"
            />
            <span className="font-black text-sm uppercase tracking-tight leading-none" style={{fontFamily: 'Outfit, sans-serif'}}>
              <span className="text-[#D4AF37]">WÔMA</span>
              <span className="block text-[9px] text-slate-400 font-bold">de ARTEKNOS</span>
            </span>
            <button
              onClick={aboutModal.openManually}
              className="ml-1 w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white flex items-center justify-center transition-colors"
              data-testid="dashboard-about-btn"
              title="Qui sommes-nous ?"
              aria-label="Qui sommes-nous ?"
            >
              <Info size={12} />
            </button>
          </div>
          <div className="flex gap-1.5 sm:gap-3 items-center min-w-0">
            <button
              onClick={() => navigate('/credits')}
              className="bg-[#D4AF37]/20 px-2.5 sm:px-3 py-1.5 rounded-full border border-[#D4AF37]/30 text-[10px] font-bold text-[#D4AF37] hover:bg-[#D4AF37]/30 flex items-center gap-1.5 whitespace-nowrap shrink-0"
              data-testid="credits-btn"
            >
              <Wallet className="w-3.5 h-3.5" />
              {creditBalance.toLocaleString()} F
            </button>
            <Button onClick={resetOnboarding} variant="ghost" size="sm" className="rounded-full text-white/60 hover:text-white hover:bg-white/5 hidden sm:inline-flex" title="Revoir le tutoriel" data-testid="help-btn">
              <HelpCircle className="w-5 h-5" />
            </Button>
            <Button onClick={() => navigate('/')} variant="ghost" className="rounded-full text-white/70 hover:text-white hover:bg-white/5 hidden md:inline-flex" data-testid="nav-home-btn">Accueil</Button>
            <Button onClick={onLogout} variant="ghost" size="sm" className="rounded-full text-white/60 hover:text-white hover:bg-white/5 hidden md:inline-flex" data-testid="dashboard-logout-btn">Déconnexion</Button>
            <AccountMenu user={user} onUserUpdate={onUserUpdate} onLogout={onLogout} />
          </div>
        </div>
      </nav>

      <div className="px-4 md:px-8 lg:px-12 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-end mb-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="dashboard-title">Tableau de bord</h2>
              <p className="text-gray-400 text-sm mt-1">Gérez vos commandes et rendez-vous</p>
            </div>
            <button
              onClick={() => navigate('/orders/new')}
              className="bg-[#003594] hover:bg-[#003380] text-white p-3 rounded-2xl shadow-lg shadow-blue-900/30 transition-all"
              data-testid="quick-action-new-order-btn"
            >
              <Package className="w-5 h-5" />
            </button>
          </div>

          {/* Multi-rôles : sélecteur + bouton d'activation d'un nouveau rôle */}
          <div className="mb-6 flex flex-col gap-4 scroll-mt-24" id="ajouter-profil">
            <div className="flex items-center justify-end">
              <RoleSwitcher user={user} onUserUpdate={onUserUpdate} />
            </div>
            <AddRoleCard user={user} onUserUpdate={onUserUpdate} />
          </div>

          <div className="mb-8">
            <ClientMapPanel />
          </div>

          <div className="mb-8 scroll-mt-24" id="portefeuille">
            <WalletWaveCard user={user} />
          </div>

          <div className="mb-8 scroll-mt-24" id="parametres">
            <DeliverySharingToggle user={user} />
          </div>

          {/* Quick actions — 4 cards luxury */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <button onClick={() => navigate('/orders/new')} className="bg-[#1E293B] border border-white/5 rounded-[2rem] p-5 hover:border-[#D4AF37]/40 transition-all text-left group" data-testid="quick-action-new-order">
              <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[#003594] mb-3 group-hover:text-[#D4AF37] transition-colors">
                <Package className="w-5 h-5" />
              </div>
              <p className="font-black text-sm uppercase text-white">Nouvelle commande</p>
              <p className="text-[11px] text-gray-400 mt-1">Créer une livraison</p>
            </button>

            <button onClick={() => navigate('/artisans')} className="bg-[#1E293B] border border-white/5 rounded-[2rem] p-5 hover:border-[#D4AF37]/40 transition-all text-left group" data-testid="quick-action-appointments">
              <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[#D4AF37] mb-3">
                <Clock className="w-5 h-5" />
              </div>
              <p className="font-black text-sm uppercase text-white">Artisans</p>
              <p className="text-[11px] text-gray-400 mt-1">Rendez-vous & projets</p>
            </button>

            <button onClick={() => navigate('/products')} className="bg-[#1E293B] border border-white/5 rounded-[2rem] p-5 hover:border-[#D4AF37]/40 transition-all text-left group" data-testid="quick-action-products">
              <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-emerald-400 mb-3">
                <Truck className="w-5 h-5" />
              </div>
              <p className="font-black text-sm uppercase text-white">Boutiques</p>
              <p className="text-[11px] text-gray-400 mt-1">Produits partenaires</p>
            </button>

            <button onClick={() => navigate('/credits')} className="bg-gradient-to-br from-[#D4AF37]/20 to-[#003594]/20 border border-[#D4AF37]/40 rounded-[2rem] p-5 hover:border-[#D4AF37] transition-all text-left relative overflow-hidden" data-testid="quick-action-credits">
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-[#D4AF37] rounded-full blur-[30px] opacity-30"></div>
              <div className="relative">
                <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/20 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] mb-3">
                  <CreditCard className="w-5 h-5" />
                </div>
                <p className="font-black text-sm uppercase text-white">Mes Crédits</p>
                <p className="text-xl font-black text-[#D4AF37] mt-1">{creditBalance.toLocaleString()} FCFA</p>
              </div>
            </button>
          </div>

          {/* Orders list */}
          <div className="bg-[#1E293B] border border-white/5 rounded-[2.5rem] p-6 shadow-xl">
            <div className="flex justify-between items-center mb-5">
              <h3 className="font-black text-base uppercase tracking-tight text-white" data-testid="orders-section-title">Mes commandes</h3>
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">{orders.length} au total</span>
            </div>

            {isLoading ? (
              <p className="text-center py-10 text-gray-500">Chargement…</p>
            ) : orders.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 mx-auto mb-4 flex items-center justify-center">
                  <Package className="w-8 h-8 text-gray-600" />
                </div>
                <p className="text-gray-400 mb-5" data-testid="no-orders-message">Aucune commande pour le moment</p>
                <Button onClick={() => navigate('/orders/new')} className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0F172A] hover:opacity-90 rounded-full px-6 py-3 font-black uppercase tracking-widest text-xs shadow-xl shadow-[#D4AF37]/20" data-testid="create-first-order-btn">
                  Créer ma première commande
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => {
                  const isActive = canTrackOrder(order.status) && order.status !== 'delivered';
                  return (
                    <div key={order.id} className="bg-[#0F172A] border border-white/5 rounded-[2rem] p-5 hover:border-[#D4AF37]/30 transition-all" data-testid={`order-card-${order.id}`}>
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Commande #{order.id.substring(0, 8)}</p>
                          <p className="text-xs font-bold mt-1 text-white">{order.delivery_address}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase flex items-center gap-1.5 ${
                          order.status === 'delivered'
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : order.status === 'cancelled'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-[#003594]/20 text-blue-400'
                        }`}>
                          {isActive && <span className="animate-pulse w-1.5 h-1.5 bg-current rounded-full"></span>}
                          {getStatusLabel(order.status)}
                        </span>
                      </div>

                      <div className="text-xs space-y-1 text-gray-400 mb-4">
                        <p><span className="text-gray-500">De:</span> {order.pickup_address}</p>
                        {order.courier_name && (
                          <p><span className="text-gray-500">Livreur:</span> <span className="text-[#D4AF37] font-bold">{order.courier_name}</span></p>
                        )}
                      </div>

                      <div className="flex justify-between items-center pt-4 border-t border-white/5">
                        <span className="text-[#D4AF37] font-black text-sm">{order.total_amount?.toLocaleString()} FCFA</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/tracking?id=${order.id}`)}
                            className={`p-2 rounded-xl transition-colors ${
                              isActive
                                ? 'bg-[#003594] text-white hover:bg-[#003380]'
                                : 'bg-white/5 text-gray-400 hover:bg-white/10'
                            }`}
                            data-testid={`track-order-${order.id}`}
                            title={isActive ? 'Suivre en direct' : 'Voir sur la carte'}
                          >
                            <MapPin className="w-4 h-4"/>
                          </button>
                          {hasActiveCourier(order) && (
                            <button
                              onClick={() => callCourier(order.id)}
                              className="p-2 bg-[#D4AF37]/20 rounded-xl text-[#D4AF37] hover:bg-[#D4AF37]/30 transition-colors"
                              data-testid={`call-courier-${order.id}`}
                              title="Appeler livreur"
                            >
                              <Phone className="w-4 h-4"/>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
